import request from '@/utils/request'

  export interface FileNode {
    name: string
    isDirectory: boolean
    items?: FileNode[]
    error: boolean
    path?: string
    fullPath?: string
  }

export interface FileTreeResponse {
  name: string
  isDirectory: boolean
  items: FileNode[]
  error: boolean
}

export interface UploadResponse {
  success: boolean
  message: string
  file?: FileNode
}

export interface CreateFolderRequest {
  create_path: string
  folder_name: string
}

export class FileManagementService {
  private baseUrl = 'file_manager'

  /**
   * Получить дерево файлов
   */
  async getFileTree(path: string = '/'): Promise<FileTreeResponse> {
    try {
      const response: any = await request({
        url: `${this.baseUrl}/tree`,
        method: 'get',
      })

      return response
    } catch (error) {
      console.error('FileManagementService: Ошибка при получении дерева файлов:', error)
      // Возвращаем пустую структуру в случае ошибки
      return {
        name: path === '/' ? 'root' : path.split('/').pop() || 'unknown',
        isDirectory: true,
        items: [],
        error: true
      }
    }
  }

  /**
   * Скачать файл
   */
  async downloadFile(file_path: string): Promise<void> {
    try {
      const urlString = `${this.baseUrl}/file?file_path=${file_path}`

      const response = await request({
        url: urlString,
        method: 'get',
        responseType: 'blob'
      })

      const blobUrl = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = blobUrl
      link.setAttribute('download', file_path.split('/').pop() || 'file')
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(blobUrl)
    } catch (error) {
      console.error('Ошибка при скачивании файла:', error)
      throw error
    }
  }

  async downloadFileBlob(file_path: string): Promise<Blob> {
    try {
      const urlString = `${this.baseUrl}/file?file_path=${file_path}`

      const response = await request({
        url: urlString,
        method: 'get',
        responseType: 'blob'
      })

      return response.data as Blob
    } catch (error) {
      console.error('Ошибка при скачивании файла:', error)
      throw error
    }
  }

  /**
   * Создать папку
   */
  async createFolder(requestBody: CreateFolderRequest): Promise<{ success: boolean; message: string }> {
    try {
      await request({
        url: `${this.baseUrl}/create_folder`,
        method: 'post',
        data: requestBody
      })
      return {
        success: true,
        message: 'Папка успешно создана'
      }
    } catch (error) {
      console.error('Ошибка при создании папки:', error)
      let errorMessage = 'Ошибка при создании папки';
      if (error && typeof error === 'object' && 'response' in error) {
        const err = error as { response?: { data?: { message?: string } } };
        errorMessage = err.response?.data?.message || errorMessage;
      }
      return {
        success: false,
        message: errorMessage
      }
    }
  }

  /**
   * Создать файл
   */
  async createFile(file: File, create_path: string): Promise<{ success: boolean; message: string }> {
    try {
      const encodedFilename = encodeURIComponent(file.name)
      
      const headers = {
        'Content-Type': 'application/octet-stream'
      }

      await request({
        url: `${this.baseUrl}/create_file?create_path=${create_path}&filename=${encodedFilename}`,
        method: 'post',
        data: file,
        headers: headers
      })
      
      return {
        success: true,
        message: 'Файл успешно создан'
      }
    } catch (error) {
      console.error('Ошибка при создании файла:', error)
      let errorMessage = 'Ошибка при создании файла'
      if (error && typeof error === 'object' && 'response' in error) {
        const err = error as { response?: { data?: { message?: string } } }
        errorMessage = err.response?.data?.message || errorMessage
      }
      return {
        success: false,
        message: errorMessage
      }
    }
  }


  /**
   * Удалить файл
   */
  async deleteFile(file_path: string): Promise<{ success: boolean; message: string }> {
    try {
      const urlString = `${this.baseUrl}/delete_file?file_path=${file_path}`

      await request({
        url: urlString,
        method: 'delete'
      })
      return {
        success: true,
        message: 'Файл успешно удален'
      }
    } catch (error) {
      console.error('Ошибка при удалении файла:', error)
      let errorMessage = 'Ошибка при удалении файла';
      if (error && typeof error === 'object' && 'response' in error) {
        const err = error as { response?: { data?: { message?: string } } };
        errorMessage = err.response?.data?.message || errorMessage;
      }
      return {
        success: false,
        message: errorMessage
      }
    }
  }

  /**
   * Удалить папку
   */
  async deleteFolder(folder_path: string): Promise<{ success: boolean; message: string }> {
    try {
      const urlString = `${this.baseUrl}/delete_folder?folder_path=${folder_path}`

      await request({
        url: urlString,
        method: 'delete'
      })
      return {
        success: true,
        message: 'Папка успешно удалена'
      }
    } catch (error) {
      console.error('Ошибка при удалении папки:', error)
      let errorMessage = 'Ошибка при удалении папки';
      if (error && typeof error === 'object' && 'response' in error) {
        const err = error as { response?: { data?: { message?: string } } };
        errorMessage = err.response?.data?.message || errorMessage;
      }
      return {
        success: false,
        message: errorMessage
      }
    }
  }

  async renameItem(target_path:string, new_name:string): Promise<{ success: boolean; message: string }> {
    try{
      const urlString = `${this.baseUrl}/rename?target_path=${target_path}&new_name=${encodeURIComponent(new_name)}`
      console.log('Renaming item with URL:', urlString)

      await request({
        url: urlString,
        method: 'post',
        data: {}
      })
      console.log('Rename request successful')
      return {
        success: true,
        message: 'Переименование успешно выполнено'
      }
    } catch (error) {
      console.error('Ошибка при переименовании:', error)
      let errorMessage = 'Ошибка при удалении переименовании';
      if (error && typeof error === 'object' && 'response' in error) {
        const err = error as { response?: { data?: { message?: string } } };
        errorMessage = err.response?.data?.message || errorMessage;
      }
      return {
        success: false,
        message: errorMessage
      }
    }
  }

  async cpMoveItem(target_path: string, dest_path: string, is_move: boolean): Promise<{ success: boolean; message: string }>{
    // Перемещение и копирование работает по логике линукса, так как хранилище на нём
    // target_path - путь к копируемому/перемещаемому файлу
    // dest_path - путь к месту назначения
    // is_move - флаг обозначающий, какое именно действие производится. Если его вообще не отправить, или отправить false, то будет копирование, иначе - перемещение
    // При копировании если в папке назначения не указать в конце название копируемой папки, то скопируется содержимое папки из target_path в папку из dest_path. Если указать название папки, хоть его ещё и не существует в dest_path, то скопируется сама папка
    try{
      const urlString = `${this.baseUrl}/copy_move_data`
      console.log('moving item:')

      await request({
        url: urlString,
        method: 'post',
        data: {
          target_path: target_path,
          dest_path: dest_path,
          is_move: is_move
        }
      })
      console.log('moving request successful')
      if (is_move){
        return {
          success: true,
          message: 'Перемещение успешно выполнено'
        }
      } else {
        return {
          success: true,
          message: 'копирование успешно выполнено'
        }
      }
    } catch (error) {
      let errorMessage = '';
      if(is_move){
        console.error('Ошибка при перемещении файла:', error)
        errorMessage = 'Ошибка при перемещении файла'
      } else{
        console.error('Ошибка при копировании файла:', error)
        errorMessage = 'Ошибка при копировании файла'
      }
      
      if (error && typeof error === 'object' && 'response' in error) {
        const err = error as { response?: { data?: { message?: string } } };
        errorMessage = err.response?.data?.message || errorMessage;
      }
      return {
        success: false,
        message: errorMessage
      }
    }
  }

}



export default new FileManagementService()
